import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Email,
  Phone,
  Business,
  CalendarToday,
  Security,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useUser } from '../hooks';
import { colors } from '../theme/theme';

export const UserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading } = useUser(Number(id));

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography color="text.secondary">User not found</Typography>
        </Box>
      </AdminLayout>
    );
  }

  const profile = (user as any);
  const roles = profile?.roles || [];
  const permissions = profile?.permissions || [];
  const rolePermissions = roles.flatMap((r: any) => r.permissions?.map((p: any) => p.name) || []);
  const allPerms = Array.from(new Set([...rolePermissions, ...permissions.map((p: any) => p.name)]));

  return (
    <AdminLayout>
      <Box>
        <PageHeader title="User Details" subtitle={`${profile.first_name} ${profile.last_name}`}
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Users', path: '/users' },
            { label: `${profile.first_name} ${profile.last_name}` },
          ]}
        />

        <Grid container spacing={3}>
          {/* Profile Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: colors.primary.main, fontSize: 40 }}>
                  {profile.first_name?.[0]}{profile.last_name?.[0]}
                </Avatar>
                <Typography variant="h5" fontWeight={700}>
                  {profile.first_name} {profile.middle_name ? profile.middle_name + ' ' : ''}{profile.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {profile.email}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                  {roles.map((r: any) => (
                    <Chip key={r.id} label={r.name} size="small" color="primary" sx={{ textTransform: 'capitalize' }} />
                  ))}
                  {roles.length === 0 && <Chip label="No role assigned" size="small" variant="outlined" />}
                </Box>
                <Chip
                  label={profile.is_active || profile.status === 'active' ? 'Active' : 'Inactive'}
                  color={profile.is_active || profile.status === 'active' ? 'success' : 'default'}
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Details */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>Account Details</Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Email sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{profile.email}</Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Phone sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{profile.profile?.phone || 'Not set'}</Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Business sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">County</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{profile.county?.name || 'N/A'}</Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Security sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Account Type</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500} sx={{ textTransform: 'capitalize' }}>{profile.user_type || 'N/A'}</Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Account Created</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 18, color: colors.grey[500] }} />
                      <Typography variant="caption" color="text.secondary">Last Login</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {profile.last_login_at ? new Date(profile.last_login_at).toLocaleString() : 'Never'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Permissions */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Permissions</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {allPerms.length} permission{allPerms.length !== 1 ? 's' : ''} granted
                  {roles.length > 0 ? ` via role "${roles.map((r: any) => r.name).join(', ')}"` : ' (direct assignment)'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {allPerms.length > 0 ? allPerms.map((perm: string, i: number) => (
                    <Chip key={i} label={perm.replace(/_/g, ' ')} size="small" variant="outlined"
                      sx={{ fontSize: '0.75rem', textTransform: 'capitalize' }} />
                  )) : (
                    <Typography variant="body2" color="text.secondary">No permissions assigned</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </AdminLayout>
  );
};
